import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Drawer as BaseDrawer } from "@base-ui/react/drawer";
import { DismissIcon } from "../../primitives/Icons/Icons";
import { sizeOf } from "../../utils/propTypes";
import css from "./Drawer.module.css";

const SWIPE_MAP = {
  right: "right",
  left: "left",
  bottom: "down",
  top: "up",
};

const DrawerSideContext = React.createContext("right");

const DrawerRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  modal = true,
  side = "right",
  children,
  ...rest
}) => (
  <DrawerSideContext.Provider value={side}>
    <BaseDrawer.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      modal={modal}
      swipeDirection={SWIPE_MAP[side]}
      {...rest}
    >
      {children}
    </BaseDrawer.Root>
  </DrawerSideContext.Provider>
);

DrawerRoot.displayName = "DrawerRoot";

DrawerRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  modal: PropTypes.oneOf([true, false, "trap-focus"]),
  side: PropTypes.oneOf(["right", "left", "bottom", "top"]),
  children: PropTypes.node,
};

const DrawerTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseDrawer.Trigger
      ref={ref}
      className={clsx(css.trigger, className)}
      {...rest}
    >
      {children}
    </BaseDrawer.Trigger>
  ),
);

DrawerTrigger.displayName = "DrawerTrigger";

DrawerTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerClose = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseDrawer.Close
      ref={ref}
      aria-label="Close"
      className={clsx(css.close, className)}
      {...rest}
    >
      {children || <DismissIcon />}
    </BaseDrawer.Close>
  ),
);

DrawerClose.displayName = "DrawerClose";

DrawerClose.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

// Drawer.Popup renders its own Backdrop inside the portal — do NOT also render
// one alongside a Popup, or the scrim doubles up (twice the dim, twice the
// blur). This part exists only for layouts that compose Portal/Popup by hand.
const DrawerBackdrop = React.forwardRef(({ className, ...rest }, ref) => (
  <BaseDrawer.Backdrop
    ref={ref}
    className={clsx(css.backdrop, className)}
    {...rest}
  />
));

DrawerBackdrop.displayName = "DrawerBackdrop";

DrawerBackdrop.propTypes = {
  className: PropTypes.string,
};

const DrawerPopup = React.forwardRef(
  ({ size = "md", className, children, ...rest }, ref) => {
    const side = React.useContext(DrawerSideContext);
    const isSheet = side === "bottom" || side === "top";

    return (
      <BaseDrawer.Portal>
        <DrawerBackdrop />
        <BaseDrawer.Viewport>
          <BaseDrawer.Popup
            ref={ref}
            className={clsx(css.popup, css[`size-${size}`], className)}
            data-side={side}
            {...rest}
          >
            {isSheet && <div className={css.handle} />}
            {children}
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    );
  },
);

DrawerPopup.displayName = "DrawerPopup";

DrawerPopup.propTypes = {
  size: sizeOf("sm", "md", "lg", "full"),
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerTitle = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseDrawer.Title
      ref={ref}
      className={clsx(css.title, className)}
      {...rest}
    >
      {children}
    </BaseDrawer.Title>
  ),
);

DrawerTitle.displayName = "DrawerTitle";

DrawerTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerDescription = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseDrawer.Description
      ref={ref}
      className={clsx(css.description, className)}
      {...rest}
    >
      {children}
    </BaseDrawer.Description>
  ),
);

DrawerDescription.displayName = "DrawerDescription";

DrawerDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerHeader = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.header, className)} {...rest}>
      {children}
    </div>
  ),
);

DrawerHeader.displayName = "DrawerHeader";

DrawerHeader.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerBody = React.forwardRef(({ className, children, ...rest }, ref) => (
  <div ref={ref} className={clsx(css.body, className)} {...rest}>
    {children}
  </div>
));

DrawerBody.displayName = "DrawerBody";

DrawerBody.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DrawerActions = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.actions, className)} {...rest}>
      {children}
    </div>
  ),
);

DrawerActions.displayName = "DrawerActions";

DrawerActions.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Drawer = {
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Close: DrawerClose,
  Popup: DrawerPopup,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Backdrop: DrawerBackdrop,
  Header: DrawerHeader,
  Body: DrawerBody,
  Actions: DrawerActions,
};

export default Drawer;
